import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'

import store from '@/store'
import auth from '@/services/servicioAutenticacion'
import { esSubdominioLogin, construirUrlEmpresa, construirUrlLogout, obtenerRucDesdeSubdominio } from '@/utils/subdominio'

import ClientLayout from '@/layouts/ClientLayout.vue'
import ClientDashboard from '@/views/dashboard/ClientDashboard.vue'
import InicioSesion from '@/views/auth/InicioSesion.vue'
import RecuperarClave from '@/views/auth/RecuperarClave.vue'
import ClientCambioClaveToken from '@/views/auth/ClientCambioClaveToken.vue'
import ModuleRestricted from '@/views/system/ModuleRestricted.vue'
import SubscriptionInactive from '@/views/system/SubscriptionInactive.vue'

// Clientes
import ClientClientesListado from '@/views/clientes/ClientClientesListado.vue'
import ClientClientesNuevo from '@/views/clientes/ClientClientesNuevo.vue'
import ClientClientesEditar from '@/views/clientes/ClientClientesEditar.vue'
import ClientClientesVer from '@/views/clientes/ClientClientesVer.vue'
import ClientCargaMasivaClientes from '@/views/clientes/ClientCargaMasivaClientes.vue'

// Proveedores
import ClientProveedoresListado from '@/views/proveedores/ClientProveedoresListado.vue'
import ClientProveedoresNuevo from '@/views/proveedores/ClientProveedoresNuevo.vue'
import ClientCargaMasivaProveedores from '@/views/proveedores/ClientCargaMasivaProveedores.vue'

// Facturación
import ClientVentas from '@/views/ventas/ClientVentas.vue'

// Proformas
import ClientVentasProformar from '@/views/ventas/ClientVentasProformar.vue'
import ProformaNueva from '@/views/ventas/ProformaNueva.vue'
import HistorialVentas from '@/views/ventas/HistorialVentas.vue'
import ClientDocumentosEmitidos from '@/views/ventas/ClientDocumentosEmitidos.vue'

// Inventario
import ClientInventarioProductos from '@/views/inventario/ClientInventarioProductos.vue'
import ClientInventarioCategorias from '@/views/inventario/ClientInventarioCategorias.vue'
import ClientInventarioAlmacenes from '@/views/inventario/ClientInventarioAlmacenes.vue'
import ClientInventarioMarcas from '@/views/inventario/ClientInventarioMarcas.vue'
import ClientInventarioUnidadesMedida from '@/views/inventario/ClientInventarioUnidadesMedida.vue'
import ClientInventarioStock from '@/views/inventario/ClientInventarioStock.vue'

// Ventas
import ClientVentasPedidos from '@/views/ventas/ClientVentasPedidos.vue'

// Compras
import ClientComprasOrdenes from '@/views/compras/ClientComprasOrdenes.vue'
import ClientComprasRegistrar from '@/views/compras/ClientComprasRegistrar.vue'
import ClientComprasCargaMasiva from '@/views/compras/ClientComprasCargaMasiva.vue'
import ClientEstadoCuentaProveedor from '@/views/compras/ClientEstadoCuentaProveedor.vue'

// Ventas - nuevos documentos
import ClientGuiaRemision from '@/views/ventas/ClientGuiaRemision.vue'
import ClientNotaCredito from '@/views/ventas/ClientNotaCredito.vue'

// Clientes - módulo
import ClientEstadoCuenta from '@/views/clientes/ClientEstadoCuenta.vue'

// Contabilidad
import ClientContabilidadAsientos from '@/views/contabilidad/ClientContabilidadAsientos.vue'

// Reportes
import ClientReportesVentas from '@/views/reportes/ClientReportesVentas.vue'
import ClientReportesUtilidadAproximada from '@/views/reportes/ClientReportesUtilidadAproximada.vue'
import ClientReportesProductosVendidos from '@/views/reportes/ClientReportesProductosVendidos.vue'
import ClientReportesClientesDeuda from '@/views/reportes/ClientReportesClientesDeuda.vue'
import ClientReportesProveedoresDeuda from '@/views/reportes/ClientReportesProveedoresDeuda.vue'
import ClientReportesAts from '@/views/reportes/ClientReportesAts.vue'

// Bancos
import ClientBancosCuentas from '@/views/bancos/ClientBancosCuentas.vue'
import ClientBancosMovimientos from '@/views/bancos/ClientBancosMovimientos.vue'

// Cajas
import ClientCajasListado from '@/views/cajas/ClientCajasListado.vue'
import ClientCajas from '@/views/cajas/ClientCajas.vue'
import ClientCajasMovimientos from '@/views/cajas/ClientCajasMovimientos.vue'
import ClientCajasConfiguracion from '@/views/cajas/ClientCajasConfiguracion.vue'

// Configuración
import ClientConfiguracionPermisos from '@/views/configuracion/ClientConfiguracionPermisos.vue'
import ClientConfiguracionFacturacion from '@/views/configuracion/ClientConfiguracionFacturacion.vue'
import ClientConfiguracionSecuenciales from '@/views/configuracion/ClientConfiguracionSecuenciales.vue'
import ClientConfiguracionCorreos from '@/views/configuracion/ClientConfiguracionCorreos.vue'
import ClientConfiguracionPlantillas from '@/views/configuracion/ClientConfiguracionPlantillas.vue'
import ClientConfiguracionUsuarios from '@/views/configuracion/ClientConfiguracionUsuarios.vue'
import ClientConfiguracionImpresiones from '@/views/configuracion/ClientConfiguracionImpresiones.vue'

// Personal
import ClientPersonalListado from '@/views/personal/ClientPersonalListado.vue'
import ClientPersonalNuevo from '@/views/personal/ClientPersonalNuevo.vue'
import ClientPersonalEditar from '@/views/personal/ClientPersonalEditar.vue'

const applyModule = (moduleCode, routes) => routes.map(route => ({
  ...route,
  meta: {
    requiresAuth: true,
    moduleCode,
    ...(route.meta || {})
  }
}))

// Control de validación de suscripción (temporalmente desactivada cuando true)
const SKIP_SUBSCRIPTION_VALIDATION = true
// Control de validación de módulos (temporalmente desactivada cuando true)
const SKIP_MODULE_VALIDATION = true

const childrenRoutes = [
  {
    path: 'perfil',
    name: 'ClientProfile',
    component: () => import('@/views/perfil/ClientProfile.vue')
  },
  {
    path: 'configuracion-usuario',
    name: 'ClientSettings',
    component: () => import('@/views/perfil/ClientSettings.vue')
  },
  
  // 1. Inicio
  ...applyModule('dashboard', [
    { path: '', redirect: { name: 'ClientDashboard' } },
    { path: 'dashboard', name: 'ClientDashboard', component: ClientDashboard },
    { path: 'indicadores', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'notificaciones', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'accesos-rapidos', component: () => import('@/views/system/Placeholder.vue') }
  ]),

  // 2. Personas
  ...applyModule('personas', [
    { path: 'clientes/estado-cuenta', component: ClientEstadoCuenta },
    { path: 'personas/clientes', component: ClientClientesListado },
    { path: 'personas/clientes/nuevo', component: ClientClientesNuevo },
    { path: 'clientes/nuevo', component: ClientClientesNuevo }, // Alias para acceso directo
    { path: 'personas/clientes/editar/:id', component: ClientClientesEditar },
    { path: 'personas/clientes/ver/:id', component: ClientClientesVer },
    { path: 'personas/clientes/carga-masiva', component: ClientCargaMasivaClientes },
    { path: 'personas/proveedores', component: ClientProveedoresListado },
    { path: 'personas/proveedores/nuevo', component: ClientProveedoresNuevo },
    { path: 'proveedores/nuevo', component: ClientProveedoresNuevo }, // Alias para acceso directo
    { path: 'personas/proveedores/carga-masiva', component: ClientCargaMasivaProveedores },
    { path: 'personas/proveedores/editar/:id', component: () => import('@/views/proveedores/ClientProveedoresEditar.vue') },
    { path: 'proveedores/editar/:id', component: () => import('@/views/proveedores/ClientProveedoresEditar.vue') }, // Alias para acceso directo
    { path: 'personas/proveedores/ver/:id', component: () => import('@/views/proveedores/ClientProveedoresVer.vue') },
    { path: 'proveedores/ver/:id', component: () => import('@/views/proveedores/ClientProveedoresVer.vue') }, // Alias para acceso directo
    { path: 'personas/empleados', component: ClientPersonalListado },
    { path: 'personas/empleados/nuevo', component: ClientPersonalNuevo },
    { path: 'personas/empleados/editar/:id', component: ClientPersonalEditar },
    { path: 'personas/usuarios', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'personas/roles-permisos', component: ClientConfiguracionPermisos },
    { path: 'personas/direcciones', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'personas/historial', component: () => import('@/views/system/Placeholder.vue') }
  ]),

  // 3. Ventas
  ...applyModule('ventas', [
    { path: 'ventas/cotizaciones', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'ventas/pedidos', component: ClientVentasPedidos },
    { path: 'ventas/facturacion', component: ClientVentas },
    { path: 'ventas/documentos-emitidos', name: 'ClientDocumentosEmitidos', component: ClientDocumentosEmitidos },
    { path: 'ventas/historial', component: HistorialVentas },
    { path: 'ventas/guias-remision', component: ClientGuiaRemision },
    { path: 'ventas/notas-credito', component: ClientNotaCredito },
    { path: 'ventas/entregas', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'ventas/cobros', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'ventas/pos', component: () => import('@/views/system/Placeholder.vue') }
  ]),

  // PF. Proformas
  ...applyModule('proformas', [
    { path: 'proformas', component: ClientVentasProformar },
    { path: 'proformas/nueva', component: ProformaNueva },
    { path: 'proformas/editar/:id', component: ProformaNueva }
  ]),

  // 4. Compras
  ...applyModule('compras', [
    { path: 'compras/solicitudes', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'compras/registrar', component: ClientComprasRegistrar },
    { path: 'compras/ordenes', component: ClientComprasOrdenes },
    { path: 'compras/ordenes/:id', component: ClientComprasOrdenes },
    { path: 'compras/estado-cuenta', component: ClientEstadoCuentaProveedor },
    { path: 'compras/recepcion', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'compras/pagos', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'compras/historial', component: () => import('@/views/system/Placeholder.vue') }
  ]),

  // 5. Inventario
  ...applyModule('inventario', [
    { path: 'inventario/productos', component: ClientInventarioProductos },
    { path: 'inventario/productos/nuevo', component: () => import('@/views/inventario/InventarioNuevoProducto.vue') },
    { path: 'inventario/editar/:id', component: () => import('@/views/inventario/InventarioEditarProducto.vue') },
    { path: 'inventario/categorias', component: ClientInventarioCategorias },
    { path: 'inventario/almacenes', component: ClientInventarioAlmacenes },
    { path: 'inventario/marcas', component: ClientInventarioMarcas },
    { path: 'inventario/unidades-medida', component: ClientInventarioUnidadesMedida },
    { path: 'inventario/movimientos', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'inventario/transferencias', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'inventario/ajustes', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'inventario/stock', component: ClientInventarioStock },
    { path: 'inventario/carga-masiva', component: ClientComprasCargaMasiva }
  ]),

  // 6. Finanzas
  ...applyModule('finanzas', [
    { path: 'finanzas/cuentas-cobrar', component: () => import('@/views/finanzas/ClientCuentasCobrar.vue') },
    { path: 'finanzas/cuentas-pagar', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'finanzas/flujo-caja', component: ClientCajasListado },
    { path: 'finanzas/bancos', component: ClientBancosCuentas },
    { path: 'finanzas/bancos/:id/movimientos', name: 'ClientBancosMovimientos', component: ClientBancosMovimientos },
    { path: 'finanzas/conciliaciones', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'finanzas/presupuestos', component: () => import('@/views/system/Placeholder.vue') },

    // Rutas de control de caja
    { path: 'cajas',                         name: 'ClientCajas',              component: ClientCajas },
    { path: 'cajas/historial',               name: 'ClientCajasListado',       component: ClientCajasListado },
    { path: 'cajas/configuracion',            name: 'ClientCajasConfiguracion', component: ClientCajasConfiguracion },
    { path: 'cajas/sesiones/:sesion_id',      name: 'ClientCajasMovimientos',   component: ClientCajasMovimientos },
    { path: 'cajas/creditos',                 name: 'ClientCajasCreditos',      component: () => import('@/views/cajas/ClientCajasCreditos.vue') },
  ]),

  // 7. Contabilidad
  ...applyModule('contabilidad', [
    { path: 'contabilidad/plan-cuentas', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'contabilidad/asientos', component: ClientContabilidadAsientos },
    { path: 'contabilidad/mayor-general', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'contabilidad/balance-general', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'contabilidad/estado-resultados', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'contabilidad/cierre', component: () => import('@/views/system/Placeholder.vue') }
  ]),

  // 8. Reportes
  ...applyModule('reportes', [
    { path: 'reportes/ventas', component: ClientReportesVentas },
    { path: 'reportes/utilidad-aproximada', component: ClientReportesUtilidadAproximada },
    { path: 'reportes/productos-vendidos', component: ClientReportesProductosVendidos },
    { path: 'reportes/clientes-deuda', component: ClientReportesClientesDeuda },
    { path: 'reportes/proveedores-deuda', component: ClientReportesProveedoresDeuda },
    { path: 'reportes/ats', component: ClientReportesAts }
  ]),

  // 9. Configuración
  ...applyModule('configuracion', [
    { path: 'configuracion/sri', component: ClientConfiguracionFacturacion },
    { path: 'configuracion/secuenciales', component: ClientConfiguracionSecuenciales },
    { path: 'configuracion/correos', component: ClientConfiguracionCorreos },
    { path: 'configuracion/plantillas', component: ClientConfiguracionPlantillas },
    { path: 'configuracion/usuarios', component: ClientConfiguracionUsuarios },
    { path: 'configuracion/impresiones', component: ClientConfiguracionImpresiones },
    { path: 'configuracion/sucursales', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'configuracion/impuestos', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'configuracion/formas-pago', component: () => import('@/views/system/Placeholder.vue') },
    { path: 'configuracion/backups', component: () => import('@/views/system/Placeholder.vue') }
  ]),


]

const routes = [
  {
    path: '/iniciar-sesion',
    name: 'InicioSesion',
    component: InicioSesion,
    meta: { requiresAuth: false }
  },
  {
    path: '/recuperar-clave',
    name: 'RecuperarClave',
    component: RecuperarClave,
    meta: { requiresAuth: false }
  },
  {
    path: '/clientes/recuperar-clave',
    name: 'ClienteRecuperarClaveToken',
    component: ClientCambioClaveToken,
    meta: { requiresAuth: false }
  },
  {
    path: '/clientes/cambiar-clave',
    name: 'ClienteCambiarClaveToken',
    component: ClientCambioClaveToken,
    meta: { requiresAuth: false }
  },
  {
    path: '/upgrade',
    name: 'ModuleRestricted',
    component: ModuleRestricted,
    meta: { requiresAuth: false }
  },
  {
    path: '/subscription-inactive',
    name: 'SubscriptionInactive',
    component: SubscriptionInactive,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    redirect: '/iniciar-sesion'
  },
  {
    path: '/cerrar-sesion',
    name: 'CerrarSesion',
    meta: { requiresAuth: false },
    beforeEnter(to, from, next) {
      // Limpiar todos los tokens de sesión del cliente
      const claves = [
        'token_acceso_cliente', 'token_refresco_cliente',
        'client_access_token', 'client_refresh_token',
        'usuario_cliente', 'empresa_cliente', 'modulos_cliente'
      ]
      claves.forEach(clave => localStorage.removeItem(clave))
      next({ path: '/iniciar-sesion', replace: true })
    },
    component: { render: () => null }
  },
  {
    path: '/',
    component: ClientLayout,
    meta: { requiresAuth: true },
    children: childrenRoutes
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const requiresAuth = (route) => route.meta?.requiresAuth !== false

router.beforeEach((to, from, next) => {
  // ── Limpiar hash de handoff de tokens ─────────────────────────────────────
  // createWebHistory captura window.location al importar el módulo, ANTES de
  // que main.js pueda limpiar el hash con history.replaceState. El router
  // hace la primera navegación con el hash incluido (#t=TOKEN&r=REFRESH).
  // Interceptamos esa navegación y redirigimos a la ruta sin hash.
  if (from === START_LOCATION && to.hash && /^#t=/.test(to.hash)) {
    return next({ path: to.path, query: to.query, hash: '', replace: true })
  }
  // ──────────────────────────────────────────────────────────────────────────

  // Evitar navegación duplicada que puede provocar loops — pero NO en la
  // navegación inicial (from === START_LOCATION), donde from.path es '/' igual
  // que to.path cuando la URL está en '/', lo que bloquearía el redirect a /dashboard.
  if (from !== START_LOCATION && to.path === from.path && JSON.stringify(to.query || {}) === JSON.stringify(from.query || {})) {
    return next(false)
  }

  // Si estamos en un subdominio de empresa ({ruc}.sicecom.com) y la ruta es
  // pública (login, recuperar clave, etc.), redirigir a app.sicecom.com.
  // Las rutas de activación de cuenta (cambiar-clave) pueden funcionar en ambos subdominios.
  const rutasPublicasExcluidas = ['ClientCambioClaveToken', 'ClienteRecuperarClaveToken', 'ClienteCambiarClaveToken']
  if (!esSubdominioLogin() && !rutasPublicasExcluidas.includes(to.name)) {
    if (!auth.estaAutenticado()) {
      // Usar siempre construirUrlLogout (incluye ?accion=cerrar-sesion) para que
      // app.sicecom.com limpie su propio localStorage antes de mostrar el login.
      // Esto también soluciona la carrera entre cerrarSesion() y router.push.
      const urlLogout = construirUrlLogout()
      if (urlLogout) {
        window.location.replace(urlLogout)
        return
      }
    }
  }

  const needsAuth = to.matched.some(requiresAuth)
  const isLoggedIn = auth.estaAutenticado()

  if (needsAuth && !isLoggedIn) {
    return next({ path: '/iniciar-sesion', query: { redireccion: to.fullPath } })
  }

  if (to.name === 'InicioSesion' && isLoggedIn) {
    // Si el usuario ya está autenticado y visita app.sicecom.com,
    // redirigirlo automáticamente al subdominio de su empresa ({ruc}.sicecom.com).
    // En localhost construirUrlEmpresa devuelve null y se hace push interno.
    if (esSubdominioLogin() && !store.state.user?.is_super_admin) {
      const ruc         = store.state.user?.company?.ruc
      const tokenAcceso = localStorage.getItem('token_acceso_cliente')
      const tokenRefr   = localStorage.getItem('token_refresco_cliente')
      if (ruc && tokenAcceso) {
        const url = construirUrlEmpresa(ruc, tokenAcceso, tokenRefr, '/')
        if (url) {
          window.location.replace(url)
          return
        }
      }
    }
    return next('/')
  }

  if (!needsAuth) {
    return next()
  }

  // Validar que el usuario autenticado esté en el subdominio correcto para su empresa.
  // Si el RUC del subdominio no coincide con el RUC de su empresa, redirigirlo.
  if (!esSubdominioLogin()) {
    const rucSubdominio = obtenerRucDesdeSubdominio()
    const rucUsuario    = store.state.user?.company?.ruc
    if (rucSubdominio && rucUsuario && rucSubdominio !== rucUsuario) {
      const tokenAcceso = localStorage.getItem('token_acceso_cliente')
      const tokenRefr   = localStorage.getItem('token_refresco_cliente')
      const url = construirUrlEmpresa(rucUsuario, tokenAcceso, tokenRefr, to.fullPath)
      if (url) {
        window.location.replace(url)
        return
      }
    }
  }

  // Permitir rutas especiales sin volver a redirigirlas
  if (to.name === 'ModuleRestricted' || to.name === 'SubscriptionInactive') {
    return next()
  }

  // Verificar suscripción activa para usuarios no super admin
  const currentUser = store.getters.currentUser
  if (currentUser && !currentUser.is_super_admin) {
    if (!SKIP_SUBSCRIPTION_VALIDATION) {
      const hasActiveSubscription = store.getters.hasActiveSubscription
      if (!hasActiveSubscription) {
        // Redirigir a página de suscripción inactiva
        return next({ name: 'SubscriptionInactive' })
      }
    }
  }

  if (!SKIP_MODULE_VALIDATION) {
    const hasModuleMeta = [...to.matched].reverse().find(record => record.meta?.moduleCode)

    if (hasModuleMeta) {
      const moduleCode = hasModuleMeta.meta.moduleCode
      // El dashboard siempre está disponible para usuarios autenticados
      const esSiempreAccesible = ['dashboard'].includes(moduleCode)
      const canAccess = esSiempreAccesible || store.getters.hasModule(moduleCode)

      if (!canAccess) {
        return next({
          name: 'ModuleRestricted',
          query: { module: moduleCode, redirect: to.fullPath }
        })
      }
    }
  }

  return next()
})

export default router



